import { useEffect, useRef, useState } from 'react';
import omit from 'lodash/fp/omit';
import isEmpty from 'lodash/fp/isEmpty';
import classNames from 'classnames';
import TableViewToggles from '@rio-cloud/rio-uikit/TableViewToggles';
import { TableViewTogglesViewType } from '@rio-cloud/rio-uikit';
import { useSelectVehicleStore } from '../../services/selectVehicleSlice';
import { ColumnDetailsMap } from '../../shared/models/ColumnDetailsMap';
import { ColumnLabel } from '../../shared/models/ColumnLabel';
import { useOperationalDisciplineData } from './hooks/useOperationalDisciplineData';
import { useTableSettings } from './hooks/useTableSettings';
import { useWindowResize } from './hooks/useWindowResize';
import DashboardTableToolbar from './components/DashboardTableToolbar';
import DashboardTableBody from './components/DashboardTableBody';
import TableRowContent from './components/TableRowContent';
import { SidebarPortal } from '../../shared/components/SidebarPortal';
import { useLoaderData, useNavigate } from 'react-router';
import { IOperationalDiscipline } from './models/OperationalDiscipline';
import { useGetAssetsQuery } from '../../services/assetsApi';
import PagingButton from '../../shared/components/PagingButton';
import { extractLoaderOperationalDiscipline } from '../extractOperationalDiscipline/extractLoader';
import { ExtractSidebar } from '../extractOperationalDiscipline';

export type DashboardTableProps = {
  viewType: TableViewTogglesViewType;
};

const columnLabels: ColumnLabel = {
  date: 'Mês',
  score: 'Score do Mês',
  averageScore: 'Score Médio',
  complianceReviews: 'Cumprimento de Calendário',
  operationalCoherence: 'Coerência Operacional',
  //TODO: Remove when ready
  //responsibleInsurance: 'Histórico de Mau Uso da Garantia',
  extractLink: 'Acessar Extrato',
};

const columnsDetailsDescriptor = {
  date: {
    width: '11.6875rem'
  },
  score: {
    width: '8.8125rem'
  },
  averageScore: {
    width: '6.875rem'
  },
  complianceReviews: {
    width: '12.3125rem'
  },
  operationalCoherence: {
    width: '10.6875rem'
  },
  responsibleInsurance: {
    width: '35.3125rem'
  },
  extractLink: {
    width: '9rem'
  }
}


const disabledColumns: string[] = [];
const subRows: string[] = [];

const DashboardTableOperationalDiscipline = (props: DashboardTableProps) => {
  const { viewType: externalViewType } = props;
  const { selectedVehicle } = useSelectVehicleStore();
  const [searchValue, setSearchValue] = useState('');
  const [viewType, setViewType] = useState(externalViewType || TableViewToggles.VIEW_TYPE_TABLE);
  const [expandedRows, setExpandedRows] = useState<string[]>([]);
  const [offset, setOffset] = useState<number>(0);
  const [OperationalDisciplineItems, setOperationalDisciplineItems] = useState<IOperationalDiscipline[]>([]);
  const dashboardColumnOrder: string[] = Object.keys(columnLabels);
  const assetId = sessionStorage.getItem('assetId');
  const { currentData } = useGetAssetsQuery();
  const asset = currentData?.items.find((element: { id: any; }) => element.id == assetId);

  if(!asset){
    throw new Error("Asset data dont exists!");
  }

  //extract sidebar
  const navigate = useNavigate();
  const loaderData = useLoaderData<typeof extractLoaderOperationalDiscipline>();
  //extract sidebar

  const handleViewTypeChange = (newViewType: TableViewTogglesViewType) => setViewType(newViewType);

  const handleToggleRow = (id: string) => {
    setExpandedRows(prev => (prev.includes(id) ? prev.filter(expanded => expanded !== id) : [...prev, id]));
  };

  const {
    OperationalDisciplineResponseList,
    pagination,
    isLoading,
    error,
  } = useOperationalDisciplineData({
    externalViewType, assetId: asset?.id, offset
  });

  useEffect(() => {
    if (offset === 0) {
      setOperationalDisciplineItems(OperationalDisciplineResponseList);
    } else {
      setOperationalDisciplineItems(prevItems => [...prevItems, ...OperationalDisciplineResponseList]);
    }
  }, [OperationalDisciplineResponseList]);

  const {
    showTableSettingsDialog,
    hiddenColumns,
    columnsDetails,
    columnOrder,
    handleToggleTableSettingsDialog,
    handleColumnChange,
    handleColumnDetailsChange,
  } = useTableSettings({
    dashboardColumnOrder,
    columnsDetailsDescriptor: columnsDetailsDescriptor as ColumnDetailsMap,
    columnLabels,
    disabledColumns,
  });

  const menuRef = useRef(document.getElementsByClassName('AssetTree')[0]);
  const lcmCardRef = useRef(document.getElementsByClassName('StatsWidget-wrapper')[0]);
  const { width, screenWidth } = useWindowResize(menuRef, lcmCardRef);

  const columns = columnOrder.filter((name: string) => !hiddenColumns.includes(name) && !subRows.includes(name));
  const withoutHidden = omit(hiddenColumns);
  const filteredRows = OperationalDisciplineItems.map((item: IOperationalDiscipline) => ({ ...withoutHidden(item) })) as IOperationalDiscipline[];

  const searchResult = !searchValue
    ? filteredRows
    : filteredRows.filter((row: Partial<IOperationalDiscipline>) =>
      columns.some((col: string) => {
        const value = row[col as keyof IOperationalDiscipline];
        if (value == null) return false;
        return row[col as keyof IOperationalDiscipline]?.toString().toLowerCase().includes(searchValue.toLowerCase());
      })
    );

  const isViewTypeTable = viewType === TableViewToggles.VIEW_TYPE_TABLE;
  const tableClassNames = classNames(
    'table',
    'table-layout-fixed',
    'table-column-overflow-hidden',
    'table-bordered',
    'table-sticky',
    'table-head-filled',
    viewType === TableViewToggles.VIEW_TYPE_SINGLE_CARD && 'table-cards table-single-card',
    viewType === TableViewToggles.VIEW_TYPE_MULTI_CARDS && 'table-cards table-multi-cards'
  );

  return (
    <div>
      <SidebarPortal>
        {loaderData && (
          <ExtractSidebar
            data={loaderData.response}
            extractDate={loaderData.date}
            assetName={asset.name}
            onClose={() => {
              navigate({ search: "" });
            }}
            onBackdropClick={() => {
              navigate({ search: "" });
            }}
          />
        )}
      </SidebarPortal>
      <DashboardTableToolbar
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        viewType={viewType}
        onViewTypeChange={handleViewTypeChange}
        selectedVehicle={selectedVehicle}
        onToggleSettings={handleToggleTableSettingsDialog}
        showTableSettingsDialog={showTableSettingsDialog}
        onHideSettings={handleToggleTableSettingsDialog}
        onColumnChange={handleColumnChange}
        onColumnDetailsChange={handleColumnDetailsChange}
        columnOrder={columnOrder}
        hiddenColumns={hiddenColumns}
        columnsDetails={columnsDetails}
        columnLabels={columnLabels}
        bestPracticesColumnOrder={dashboardColumnOrder}
        disabledColumns={disabledColumns}
        hasData={!isEmpty(OperationalDisciplineItems)}
        asset={asset}
      />

      <div
        style={{
          overflowX: screenWidth < 1734 ? 'auto' : undefined,
          maxWidth: screenWidth < 1734 ? width : undefined,
          height: screenWidth < 1734 ? '40vh' : undefined,
        }}
      >
        <DashboardTableBody
          searchResult={searchResult}
          columns={columns}
          columnLabels={columnLabels}
          columnsDetails={columnsDetails}
          subRows={[]}
          tableClassNames={tableClassNames}
          isLoading={isLoading}
          isViewTypeTable={isViewTypeTable}
          screenWidth={screenWidth}
          width={width}
          expandedRows={expandedRows}
          handleToggleRow={handleToggleRow}
          TableRowContent={TableRowContent}
          assetId={assetId}
        />
        {(() => {
          if (!isLoading && !isEmpty(OperationalDisciplineItems)) {
            return (
              <PagingButton onLoad={ searchResult.length } hasMore={pagination?.total ? pagination.total : 0} onLoadMore={(amount: number) => {
                setOffset(searchResult.length);
              }} />
            )
          }
        })()}
      </div>
    </div>
  );
};

export default DashboardTableOperationalDiscipline;